var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import merge from "@tinkoff/utils/object/mergeDeep";
import FavoriteDataFetcher from "./favorite-data-fetcher";
import favoriteRequestTransformer from "./transformers/request-object-transformer";
import { abbreviateNumber } from "./number";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isDataRequest(maybeDataRequest) {
    return typeof maybeDataRequest === "object" && "chartId" in maybeDataRequest && "queryType" in maybeDataRequest;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getChartData(data, signal) {
    return __awaiter(this, void 0, void 0, function* () {
        const dataRequest = data;
        const dataSeries = data;
        if (data instanceof Promise) {
            return data;
        }
        else if (dataRequest.metrics) {
            const favoriteRequest = favoriteRequestTransformer(dataRequest);
            return FavoriteDataFetcher.fetchData(favoriteRequest, signal).catch((error) => {
                // Re-throw any error that is not an AbortError
                if (error.name !== "AbortError") {
                    throw error;
                }
                return [];
            });
        }
        return Promise.resolve(dataSeries);
    });
}
export function chartOptions(series, options = {}) {
    const defaultOptions = defaultChartOptions();
    // Apply any user-supplied options
    const chartOptions = merge(defaultOptions, options);
    // And add the data series
    return merge(chartOptions, { series });
}
export function getAllSeriesBudgets(series) {
    var _a, _b;
    const budgets = ((_b = (_a = series[0]) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.budgets) || [];
    const seriesMetrics = series.map((s) => s.metricQualified);
    return budgets.filter((budget) => seriesMetrics.includes(budget.metric));
}
export function budgetIsForSeries(budget, series) {
    return budget.metric === series.metricQualified;
}
/**
 * Determine the timespan of a chart as a number of seconds. This is typically the time btween the
 * first and last data points.
 */
export function getChartTimespan(chart) {
    if (chart.series && chart.series.length) {
        // Our preference is to determine the timespan of a chart by checking the startTime and
        // endTime of one of the series. These will be the dates that were actually queried.
        const dataSeries = chart.series[0].userOptions;
        if (dataSeries.startTime && dataSeries.endTime) {
            return (dataSeries.endTime - dataSeries.startTime) * 1000;
        }
    }
    // In some cases the series won't be available or won't have the start/end time information. In
    // these cases, we fall back to inspecting the X axis itself. This isn't the preferred method
    // because the chart's data might be incomplete, in which case we will determine the timespan
    // to be smaller than it really is.
    const xAxis = chart.xAxis[0];
    const startTime = Number(xAxis.min);
    const endTime = Number(xAxis.max);
    return endTime - startTime;
}
function defaultChartOptions() {
    return {
        chart: {
            type: "spline",
            animation: false,
            backgroundColor: "transparent",
            style: {
                fontFamily: "Gotham",
                fontSize: "16px",
            },
            zooming: {
                type: "xy",
                resetButton: {
                    theme: {
                        fill: "var(--button-bg-color)",
                        r: 4,
                        "stroke-width": 1,
                        stroke: "var(--background-color)",
                        states: {
                            hover: {
                                fill: "var(--button-bg-hover-color)",
                                style: {
                                    color: "var(--inverse-high-emphasis)",
                                },
                            },
                            select: {
                                fill: "var(--button-bg-hover-color)",
                                style: {
                                    color: "var(--inverse-high-emphasis)",
                                },
                            },
                        },
                        style: {
                            fontWeight: "800",
                            color: "var(--high-emphasis)",
                            fontFamily: "Gotham",
                        },
                    },
                },
            },
        },
        time: {
            useUTC: true,
            timezoneOffset: window.SPEEDCURVE_CONFIG.accountTimezoneOffset,
        },
        credits: {
            enabled: false,
        },
        mapNavigation: {
            enabled: false,
        },
        title: {
            text: "",
        },
        exporting: {
            enabled: false,
        },
        lang: {
            thousandsSep: ",",
        },
        xAxis: {
            type: "datetime",
            tickLength: 0,
            labels: {
                y: 25,
                style: {
                    color: "var(--high-emphasis)",
                },
                useHTML: true,
            },
            title: {
                text: null,
                style: {
                    color: "var(--medium-emphasis)",
                },
            },
            lineColor: "var(--axis-grid-line-color)",
        },
        yAxis: {
            gridLineColor: "var(--axis-grid-line-color)",
            labels: {
                style: {
                    color: "var(--high-emphasis)",
                },
                formatter: function () {
                    const { defaultLabelFormatter, dataMax } = this.axis;
                    if (typeof this.value === "number") {
                        const series = this.axis.series[0];
                        const userOptions = series.userOptions;
                        const suffix = userOptions.suffix || userOptions.xAxisUnits || "";
                        if (dataMax >= 10000) {
                            return `${abbreviateNumber(this.value)}${suffix}`;
                        }
                        return `${this.value}${suffix}`;
                    }
                    return defaultLabelFormatter.call(this);
                },
            },
            title: {
                text: undefined,
            },
            min: 0,
        },
        legend: {
            enabled: true,
            useHTML: true,
            labelFormatter: function () {
                const metaKey = navigator.userAgent.indexOf("Mac") !== -1 ? "⌘" : "Ctrl";
                return `<span title="${metaKey}+click to toggle all other series">${this.name}</span>`;
            },
            itemStyle: {
                color: "var(--medium-emphasis)",
                fontWeight: "normal",
            },
            itemHoverStyle: {
                color: "var(--high-emphasis)",
            },
            itemHiddenStyle: {
                color: "var(--low-emphasis)",
            },
            maxHeight: 100,
            navigation: {
                style: {
                    color: "var(--medium-emphasis)",
                },
            },
        },
        tooltip: {
            enabled: false,
            useHTML: true,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
                grouping: false,
            },
            series: {
                turboThreshold: 0,
                connectNulls: true,
                cursor: "pointer",
                pointPlacement: "on",
                events: {
                    legendItemClick: function (event) {
                        if (event.browserEvent.ctrlKey || event.browserEvent.metaKey) {
                            // When the user is holding the Ctrl or Cmd key, we treat this as a
                            // "toggle all other series" action.
                            const thisSeriesIndex = this.index;
                            const onlyThisVisible = this.chart.series.every((series) => !series.visible || series.index === thisSeriesIndex);
                            this.chart.series.forEach((series) => {
                                if (series.index !== thisSeriesIndex) {
                                    if (onlyThisVisible) {
                                        // When this series is the only one visible, we toggle the
                                        // other series to be visible again.
                                        series.show();
                                    }
                                    else {
                                        // Otherwise we toggle the other series so that only this
                                        // one is visible.
                                        series.hide();
                                    }
                                }
                            });
                            // Always keep the current series visible when a modifier key is held.
                            this.show();
                            return false;
                        }
                    },
                },
            },
            line: {
                marker: {
                    enabled: false,
                },
            },
            spline: {
                lineWidth: 3,
                marker: {
                    enabled: false,
                    symbol: "circle",
                    radius: 0,
                    states: {
                        hover: {
                            radius: 6,
                        },
                    },
                },
                states: {
                    hover: {
                        lineWidthPlus: 0,
                    },
                },
            },
        },
    };
}
